import styles from 'css/src/components/common/BackgroundAsset.module.scss';
import mediaBadgeStyles from 'css/src/components/common/media-badge.module.scss';

import React from 'react';
import classNames from 'classnames';

import { AssetContainerTypes, type SourceEnum } from 'js/src/client';
import MediaBadge from 'js/src/components/common/MediaBadge';
import LottieAsset from 'js/src/components/common/ui/LottieAsset';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import Video from 'js/src/components/common/Video';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';

type ImageAssetProps = {
    thumbnailUrl: string;
    width: number;
    height: number;
    source: SourceEnum;
    isEditorial: boolean | null;
    onClick?: VoidFunction;
    isSticker?: boolean;
};

const EditorialTag = () => (
    <Tooltip title="Media licensed for editorial use only." delay={{ show: 500, hide: 0 }}>
        <a
            className={classNames(
                'text-decoration-none hide-while-dragging hidden-soft',
                mediaBadgeStyles['media-badge'],
            )}
            href="https://help.lumen5.com/en/article/is-my-video-commercial-or-editorial-12en3kq/"
            target="_blank"
            rel="noreferrer"
        >
            ED
        </a>
    </Tooltip>
);

export const ImageAsset = React.memo(function ImageAsset({
    thumbnailUrl,
    width,
    height,
    source,
    isEditorial,
    onClick,
    isSticker,
}: ImageAssetProps) {
    const isPortrait = width / height < 0.8;

    const backgroundStyle = {
        backgroundImage: 'url("' + thumbnailUrl + '")',
    };

    const mediaBadge: React.ReactElement | null = SERVER.ANALYTICS?.plan_name
        .toLowerCase()
        .startsWith('team') ? null : (
        <MediaBadge source={source} assetType={AssetContainerTypes.BACKGROUND} />
    );

    return (
        <div
            className={classNames(styles['background-asset'], {
                [styles['sticker-style']]: isSticker,
            })}
        >
            <div className={classNames(styles['top-left-container'], 'hide-while-dragging')}>
                {mediaBadge}
                {isEditorial && <EditorialTag />}
            </div>
            {isPortrait && (
                <div
                    className={classNames('square-img portrait-background', {
                        'contained-background': isSticker,
                    })}
                    style={{ backgroundImage: 'url("' + thumbnailUrl + '")' }}
                />
            )}
            <div
                className={classNames('square-img', {
                    'contained-background': isSticker,
                })}
                onClick={onClick}
                style={backgroundStyle}
            />
        </div>
    );
});

type VideoAssetProps = {
    width: number;
    height: number;
    source: SourceEnum;
    posterUrl: string;
    thumbnailUrl: string;
    duration: number | null;
    isEditorial: boolean | null;
    onClick?: VoidFunction;
    isSticker?: boolean;
};

export const VideoAsset = React.memo(function VideoAsset({
    width,
    height,
    source,
    posterUrl,
    thumbnailUrl,
    duration,
    isEditorial,
    onClick,
    isSticker,
}: VideoAssetProps) {
    const isPortrait = width / height < 0.8;

    const mediaBadge: React.ReactElement | null = SERVER.ANALYTICS?.plan_name
        .toLowerCase()
        .startsWith('team') ? null : (
        <MediaBadge source={source} assetType={AssetContainerTypes.BACKGROUND} />
    );

    return (
        <div
            className={classNames(styles['background-asset'], {
                [styles['sticker-style']]: isSticker,
            })}
        >
            <div className={classNames(styles['top-left-container'], 'hide-while-dragging')}>
                <div className="d-flex flex-row">
                    {mediaBadge}
                    {isEditorial && <EditorialTag />}
                </div>
            </div>
            {!isSticker && duration && (
                <div className={classNames(styles['bottom-left-container'], 'hide-while-dragging')}>
                    <span className={mediaBadgeStyles['media-badge']}>
                        {getTimeStringFromSeconds(duration / 1000)}
                    </span>
                </div>
            )}
            {isPortrait && (
                <img className="square-img portrait-background" src={posterUrl} alt="" />
            )}
            <Video
                preview_url={thumbnailUrl}
                className="square-img"
                poster={posterUrl}
                play_on_hover={true}
                muted={true}
                loop
                onClick={onClick}
            />
        </div>
    );
});

type IconAssetProps = {
    source: SourceEnum;
    numColors: number;
    url: string;
    onClick: VoidFunction;
};

export const IconAsset = React.memo(function IconAsset({
    source,
    numColors,
    url,
    onClick,
}: IconAssetProps) {
    const iconContainerClasses = classNames(
        styles['lottie-container'],
        'd-flex justify-content-center align-items-center position-absolute top-0 start-0 end-0 bottom-0',
        `colors-${numColors}`,
        `${numColors > 1 ? 'multi' : 'single'}-color`,
    );

    return (
        <div className={classNames(styles['background-asset'], styles['icon-container'])}>
            <div className={classNames(styles['top-left-container'], 'hide-while-dragging')}>
                <MediaBadge source={source} assetType={AssetContainerTypes.ICON} />
            </div>
            <div className={iconContainerClasses}>
                <LottieAsset playOnHover={true} loop={true} path={url} onClick={onClick} />
            </div>
        </div>
    );
});

type WaveformAssetProps = IconAssetProps;

export const WaveformAsset = React.memo(function WaveformAsset({
    source,
    numColors,
    url,
    onClick,
}: WaveformAssetProps) {
    const iconContainerClasses = classNames(
        styles['lottie-container'],
        'd-flex justify-content-center align-items-center position-absolute top-0 start-0 end-0 bottom-0',
        `colors-${numColors}`,
        `${numColors > 1 ? 'multi' : 'single'}-color`,
    );

    return (
        <div className={classNames(styles['background-asset'], styles['waveform-container'])}>
            <div className={classNames(styles['top-left-container'], 'hide-while-dragging')}>
                <MediaBadge source={source} assetType={AssetContainerTypes.ICON} />
            </div>
            <div className={iconContainerClasses}>
                <LottieAsset
                    playOnHover={true}
                    loop={true}
                    path={url}
                    onClick={onClick}
                    initialFrame="middle"
                    speed={2}
                />
            </div>
        </div>
    );
});
